import React, { useState, useEffect, useRef } from 'react';
import { getToken } from '../firebase';
import { Key, Trash2, RefreshCw, PlusCircle, Clock, ClipboardCopy, AlertCircle, CheckCircle2, XCircle, Save } from 'lucide-react';

const ApiKeysModern = ({ backendUrl, userId, workgroupId, workgroupName, llmUrl }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [keyCreationMode, setKeyCreationMode] = useState(false);
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [generatingKey, setGeneratingKey] = useState(false);
  const [copiedKeyId, setCopiedKeyId] = useState(null);
  const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
  
  // Ref to track if component is mounted (for async operations)
  const isMountedRef = useRef(true);

  // Track mounted state and ensure loading state is properly managed
  useEffect(() => {
    // Initial loading safety - set to false after component is mounted
    const safetyTimeout = setTimeout(() => {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }, 1500); // Safety timeout to ensure loading eventually stops
    
    return () => {
      isMountedRef.current = false;
      clearTimeout(safetyTimeout);
    };
  }, []);

  // Load demo keys from localStorage to persist between sessions
  const loadDemoKeys = () => {
    try {
      const savedKeys = localStorage.getItem(`demo_api_keys_${workgroupId}`);
      if (savedKeys) {
        return JSON.parse(savedKeys);
      }
    } catch (e) {
      console.error("Error loading demo keys from localStorage:", e);
    }
    return [];
  };
  
  // Save demo keys to localStorage
  const saveDemoKeys = (keys) => {
    try {
      localStorage.setItem(`demo_api_keys_${workgroupId}`, JSON.stringify(keys));
    } catch (e) {
      console.error("Error saving demo keys to localStorage:", e);
    }
  };

  const fetchApiKeys = async () => {
    console.log("fetchApiKeys: Starting fetch");
    setLoading(true);
    
    try {
      // Get authentication token
      const token = await getToken();
      console.log("fetchApiKeys: Got token");
      
      // Directly use the known working endpoint
      const response = await fetch(`${backendUrl}/keys`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`API returned status: ${response.status}`);
      }
      
      // Get the response data
      const data = await response.json();
      console.log("fetchApiKeys: Received data:", data);
      
      // Check if we have apiKeys in the response
      if (data && data.apiKeys && Array.isArray(data.apiKeys)) {
        console.log("fetchApiKeys: Found apiKeys array with", data.apiKeys.length, "keys");
        
        // Update the state with the keys
        setApiKeys(data.apiKeys);
        setError(null);
      } else {
        console.warn("fetchApiKeys: No apiKeys found in response", data);
        setApiKeys([]);
      }
    } catch (error) {
      console.error("fetchApiKeys: Error fetching API keys:", error);
      setError(`Error fetching API keys: ${error.message}`);
      setApiKeys([]);
    } finally {
      console.log("fetchApiKeys: Completed");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, [workgroupId]);

  const handleRemoveKey = async (keyId, keyPrefix) => {
    if (window.confirm(`Are you sure you want to revoke API key ${keyPrefix}? This action cannot be undone.`)) {
      try {
        // Use real API endpoints
        const demoMode = false;
        
        if (!demoMode) {
          const token = await getToken();
          
          // Skip the v2 endpoint and use the original working endpoint directly
          try {
            const backendResponse = await fetch(`${backendUrl}/removeKey`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ keyId })
            });
            
            if (backendResponse.ok) {
              // Clear newly created key if it was revoked
              if (newlyCreatedKey && newlyCreatedKey.id === keyId) {
                setNewlyCreatedKey(null);
              }
  
              const updatedKeys = apiKeys.filter(key => key.id !== keyId);
              setApiKeys(updatedKeys);
              showSuccess(`API key ${keyPrefix} revoked successfully`);
              return; // Exit if successful
            }
          } catch (backendError) {
            console.error("Error with backend removeKey endpoint:", backendError);
          }
        }
        
        // Demo mode implementation
        setTimeout(() => {
          // Clear newly created key if it was revoked
          if (newlyCreatedKey && newlyCreatedKey.id === keyId) {
            setNewlyCreatedKey(null);
          }
          
          // Update keys state and localStorage
          const updatedKeys = apiKeys.filter(key => key.id !== keyId);
          setApiKeys(updatedKeys);
          saveDemoKeys(updatedKeys);
          
          showSuccess(`API key ${keyPrefix} revoked successfully (demo mode)`);
        }, 300); // Slight delay to simulate API call
        
      } catch (err) {
        showError('Error revoking API key in demo mode');
      }
    }
  };

  const generateApiKey = async (e) => {
    if (e) e.preventDefault();
    
    if (!newKeyDescription.trim()) {
      showError('Please provide a description for your API key');
      return;
    }
    
    setGeneratingKey(true);
    try {
      // Use real API endpoints
      const demoMode = false;
      
      if (!demoMode) {
        const token = await getToken();
        
        // Use the working endpoint
        try {
          console.log("Creating API key with description:", newKeyDescription.trim());
          const response = await fetch(`${llmUrl}/v2/getApiKey`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              token: token,
              workgroup: workgroupId,
              description: newKeyDescription.trim()
            })
          });
  
          if (response.ok) {
            const data = await response.json();
            console.log("API key creation response:", data);
            const apiKey = data.key;
  
            if (apiKey) {
              // Store the newly created key for display
              const newKey = {
                id: apiKey.split('.')[0], 
                prefix: apiKey.split('.')[0],
                fullKey: apiKey,
                description: newKeyDescription.trim(),
                created: new Date().toISOString(),
                workgroup: workgroupId
              };
              
              setNewlyCreatedKey(newKey);
              
              // Add to the list and save
              const updatedKeys = [...apiKeys, newKey];
              setApiKeys(updatedKeys);
              
              // Reset form
              setNewKeyDescription('');
              setKeyCreationMode(false);
              
              // Success message
              showSuccess('New API key generated successfully');
              return; // Exit early if successful
            }
          }
        } catch (error) {
          console.error("Error with real API key generation:", error);
        }
      }
      
      // Demo mode implementation with localStorage persistence
      setTimeout(() => {
        // Create a simulated key with realistic format
        const timestamp = Date.now().toString(36);
        const random = Math.random().toString(36).substring(2, 8);
        const demoKeyPrefix = `pk_${timestamp}`;
        const demoKeySuffix = `${random}${Math.random().toString(36).substring(2, 10)}`;
        const demoKey = `${demoKeyPrefix}.${demoKeySuffix}`;
        
        // Create new key object
        const newKey = {
          id: demoKeyPrefix,
          prefix: demoKeyPrefix,
          fullKey: demoKey,
          description: newKeyDescription.trim(),
          created: new Date().toISOString(),
          workgroup: workgroupId
        };
        
        // Add to localStorage and state
        const updatedKeys = [...apiKeys, newKey];
        setApiKeys(updatedKeys);
        saveDemoKeys(updatedKeys);
        
        // Set for display
        setNewlyCreatedKey(newKey);
        
        // Reset form
        setNewKeyDescription('');
        setKeyCreationMode(false);
        
        // Success message
        showSuccess('Demo API key created successfully');
      }, 500); // Small delay to simulate API call
    } catch (err) {
      showError('Error creating API key in demo mode');
    } finally {
      setTimeout(() => {
        setGeneratingKey(false);
      }, 500);
    }
  };

  const formatDate = (created) => {
    if (!created) return 'Unknown';
    
    try {
      // Handle different date formats
      const date = new Date(
        typeof created === 'object' && created._seconds 
          ? created._seconds * 1000 + (created._nanoseconds || 0) / 1000000
          : created
      );
      
      if (isNaN(date.getTime())) {
        return 'Invalid date';
      }
      
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      console.error('Error formatting date:', e);
      return String(created) || 'Unknown';
    }
  };

  const copyToClipboard = (text, keyId) => {
    // Function to show copy success feedback
    const showCopiedFeedback = () => {
      setCopiedKeyId(keyId);
      setTimeout(() => {
        if (isMountedRef.current) {
          setCopiedKeyId(null);
        }
      }, 2000);
    };
    
    try {
      // Try modern clipboard API if available
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        navigator.clipboard.writeText(text)
          .then(() => {
            showCopiedFeedback();
          })
          .catch(err => {
            console.error('Modern clipboard API failed:', err);
            // Try fallback method
            fallbackCopyMethod();
          });
      } else {
        // Use fallback if Clipboard API not available
        fallbackCopyMethod();
      }
    } catch (err) {
      console.error('Copy to clipboard error:', err);
      showError('Failed to copy to clipboard');
    }
    
    // Fallback copy method using document.execCommand
    function fallbackCopyMethod() {
      try {
        // Create temporary textarea element
        const textArea = document.createElement('textarea');
        textArea.value = text;
        
        // Make it invisible
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        
        // Select and copy
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          showCopiedFeedback();
        } else {
          throw new Error('execCommand copy failed');
        }
      } catch (err) {
        console.error('Fallback copy method failed:', err);
        showError('Could not copy to clipboard');
      }
    }
  };

  // Message handlers
  const showError = (message) => {
    setError(message);
    setSuccess(null);
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      if (isMountedRef.current) {
        setError(null);
      }
    }, 5000);
  };

  const showSuccess = (message) => {
    setSuccess(message);
    setError(null);
    
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      if (isMountedRef.current) {
        setSuccess(null);
      }
    }, 5000);
  };

  return (
    <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
      <div className="p-5 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Key className="w-5 h-5 mr-2" />
          API Keys for {workgroupName || workgroupId}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Manage API keys for programmatic access to your workgroup's data
        </p>
      </div>

      {/* Notification Area */}
      {(error || success) && (
        <div className="px-5 pt-4">
          {error && (
            <div className="flex items-center p-4 mb-4 text-sm text-red-800 bg-red-100 dark:bg-red-900/30 dark:text-red-300 rounded-lg">
              <AlertCircle className="w-5 h-5 mr-2" />
              <span>{error}</span>
            </div>
          )}
          
          {success && (
            <div className="flex items-center p-4 mb-4 text-sm text-green-800 bg-green-100 dark:bg-green-900/30 dark:text-green-300 rounded-lg">
              <CheckCircle2 className="w-5 h-5 mr-2" />
              <span>{success}</span>
            </div>
          )}
        </div>
      )}
      
      {/* Newly created key display - this is persistent until explicitly closed */}
      {newlyCreatedKey && (
        <div className="mx-5 mt-4 bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500 p-4 rounded-md shadow-sm">
          <div className="flex justify-between items-start">
            <div className="flex items-center">
              <Key className="w-5 h-5 text-blue-500 mr-2 flex-shrink-0" />
              <h4 className="text-md font-medium text-blue-700 dark:text-blue-300">Your New API Key</h4>
            </div>
            <button
              onClick={() => setNewlyCreatedKey(null)}
              className="text-blue-500 hover:text-blue-700 dark:hover:text-blue-400"
              aria-label="Close"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
          
          <p className="mt-2 mb-3 text-sm text-blue-600 dark:text-blue-400">
            <strong>Important:</strong> This is the only time you'll see the full key. Copy it now and store it securely.
          </p>
          
          <div className="flex mt-2">
            <input
              type="text"
              readOnly
              value={newlyCreatedKey.fullKey}
              className="flex-1 p-2 bg-white dark:bg-gray-700 border border-blue-300 dark:border-blue-600 rounded-l-md font-mono text-sm"
            />
            <button
              onClick={() => {
                console.log("Attempting to copy new key");
                copyToClipboard(newlyCreatedKey.fullKey, 'newkey');
              }}
              className="px-3 py-2 bg-blue-100 dark:bg-blue-800 hover:bg-blue-200 dark:hover:bg-blue-700 text-blue-700 dark:text-blue-300 font-medium rounded-r-md flex items-center"
            >
              {copiedKeyId === 'newkey' ? (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Copied!
                </>
              ) : (
                <>
                  <ClipboardCopy className="w-4 h-4 mr-1" />
                  Copy
                </>
              )}
            </button>
          </div>
          
          <div className="mt-3 text-sm text-blue-600 dark:text-blue-400">
            <p><strong>Description:</strong> {newlyCreatedKey.description}</p>
            <p><strong>Created:</strong> {formatDate(newlyCreatedKey.created)}</p>
          </div>
        </div>
      )}

      {/* Create Key Form or Button */}
      <div className="p-5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-750">
        {keyCreationMode ? (
          <form onSubmit={generateApiKey} className="space-y-4">
            <div>
              <label htmlFor="key-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Key Description
              </label>
              <input
                type="text"
                id="key-description"
                value={newKeyDescription}
                onChange={(e) => setNewKeyDescription(e.target.value)}
                disabled={generatingKey}
                className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                placeholder="e.g. Production Access, Development Key, CI/CD Pipeline"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Add a meaningful description to help identify this key later
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setKeyCreationMode(false)}
                disabled={generatingKey}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-650"
              >
                Cancel
              </button>
              
              <button
                type="submit"
                disabled={generatingKey || !newKeyDescription.trim()}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {generatingKey ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create API Key
                  </>
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="flex items-center justify-between">
            <button
              onClick={() => setKeyCreationMode(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <PlusCircle className="w-4 h-4 mr-2" />
              Create New API Key
            </button>
            
            <button
              onClick={fetchApiKeys}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-650"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh List
            </button>
          </div>
        )}
      </div>

      {/* API Keys List */}
      <div className="overflow-x-auto">
        {console.log("Current apiKeys state:", apiKeys)}
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-8 h-8 text-gray-400 dark:text-gray-500 animate-spin" />
            <span className="ml-3 text-gray-500 dark:text-gray-400">Loading API keys...</span>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="text-center py-8 bg-white dark:bg-gray-800">
            <Key className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No API keys found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Get started by creating your first API key using the button above.
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-750">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Key ID
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Description
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Created
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {Array.isArray(apiKeys) ? (
                apiKeys.map((key, index) => (
                  <tr key={key.id || index} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                    <td className="px-6 py-4 break-words whitespace-normal">
                      <div className="flex items-center">
                        <span className="font-mono text-sm text-gray-900 dark:text-white">
                          {key.prefix || key.id?.substring(0, 8) || "Unknown"}
                        </span>
                        <button 
                          onClick={() => copyToClipboard(key.prefix || key.id, key.id)}
                          className="ml-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                          title="Copy ID"
                        >
                          {copiedKeyId === key.id ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                          ) : (
                            <ClipboardCopy className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4 break-words whitespace-normal">
                      <span className="text-sm text-gray-900 dark:text-white">
                        {key.description || "No description"}
                      </span>
                    </td>
                    <td className="px-6 py-4 break-words whitespace-normal">
                      <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                        <Clock className="w-4 h-4 mr-1.5 flex-shrink-0" />
                        {formatDate(key.created)}
                      </div>
                    </td>
                    <td className="px-6 py-4 break-words whitespace-normal text-right text-sm font-medium">
                      <button
                        onClick={() => handleRemoveKey(key.id, key.prefix || key.id?.substring(0, 8) || "Unknown")}
                        className="inline-flex items-center px-3 py-1 border border-transparent rounded-md text-xs font-medium text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-300 dark:bg-red-900/30 dark:hover:bg-red-900/50"
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Revoke
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
                    Error loading API keys. Please try refreshing.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
      
      <div className="px-5 py-4 bg-gray-50 dark:bg-gray-750 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-start">
          <Key className="h-5 w-5 text-gray-400 dark:text-gray-500 mr-2 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-gray-600 dark:text-gray-400">
            <p className="mb-1">API keys provide full access to your workgroup data. Keep them secure and never share them publicly.</p>
            <p>Revoked keys cannot be restored. Create a new key if you accidentally revoke a key you still need.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeysModern;